import type { ReactElement } from 'react';
import React from 'react';
import type { Props as BaseProps } from 'feature-components/assessment-filters/base-filter';
import BaseFilter from 'feature-components/assessment-filters/base-filter';
import type { BaseGender } from 'services/assist-api/entities/gender/genders';
import { useGenders } from 'services/assist-api/entities/gender/genders';
import { isInitialLoad } from 'utils/check-query-status';

export type Props<IsMulti extends boolean> = {
  enabledIds?: number[];
  filteredIds?: number[];
  label?: BaseProps<BaseGender, IsMulti>['label'];
} & Pick<
  BaseProps<BaseGender, IsMulti>,
  'isMulti' | 'isClearable' | 'isSearchable' | 'value' | 'onChange'
>;

/**
 * Filter that is auto populated by backend defined genders
 *
 *  - fetches gender
 *  - has loading state until genders are fetched
 *  - disables genders not included in `enabledIds`
 */
export default function GenderFilter<IsMulti extends boolean = false>({
  enabledIds,
  filteredIds,
  label = 'Gender',
  isClearable = true,
  isSearchable = true,
  ...props
}: Props<IsMulti>): ReactElement {
  const genders = useGenders();

  return (
    <BaseFilter<BaseGender, IsMulti>
      label={label}
      options={genders.data?.json}
      getOptionValue={(o): string => String(o.id)}
      getOptionLabel={(o): string => o.name}
      isOptionDisabled={(o): boolean => !!enabledIds?.length && !enabledIds.includes(o.id)}
      isLoading={isInitialLoad(genders)}
      isClearable={isClearable}
      isSearchable={isSearchable}
      filteredIds={filteredIds}
      {...props}
    />
  );
}
